import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import dayjs, { DATE_FORMATS } from '../../../../utils/dateUtils';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import CommonDatePicker from '../../../../components/common-date-picker/CommonDatePicker';
import { changeSonarDate } from '../../../../redux/sonar/sonarReducer';
import { SONAR_PROJECT_RANGE_TYPE, SONAR_RANGE_TYPE } from '../../../../constants';
import { getSonarCollectDataStatus } from '../../../../redux/sonar/sonarActions';

import scss from './SonarDatePicker.module.scss';

function SonarDatePicker() {
  const { t } = useTranslation('sonar');
  const { id } = useParams();
  const dispatch = useDispatch();

  const {
    [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
    [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
  } = useSelector((state) => state.sonarDashboard.dateRanges);

  const { collectedTill, collectedFrom, rangeType } = useSelector((state) => state.sonarDashboard.collectedDataStatus);

  const handleChangeDate = (type, value) => {
    if (rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE) {
      handleChangeDailyDates(type, value);
    }

    if (rangeType === SONAR_PROJECT_RANGE_TYPE.MONTHLY_RANGE) {
      handleChangeMonthlyDates(type, value);
    }
  };

  const handleChangeDailyDates = (type, dates) => {
    const [startDate, endDate] = dates.map((date) => (date ? dayjs(date).format(DATE_FORMATS.API_DATE) : null));
    dispatch(changeSonarDate({ type, key: 'startDate', value: startDate }));
    dispatch(changeSonarDate({ type, key: 'endDate', value: endDate }));
  };

  const handleChangeMonthlyDates = (type, date) => {
    const startDate = dayjs(date).startOf('month').format(DATE_FORMATS.API_DATE);
    const endDate = dayjs(date).endOf('month').format(DATE_FORMATS.API_DATE);
    dispatch(changeSonarDate({ type, key: 'startDate', value: startDate }));
    dispatch(changeSonarDate({ type, key: 'endDate', value: endDate }));
  };

  const timeFormatsByRangeTypes = {
    [SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE]: 'dd.MM.yyyy',
    [SONAR_PROJECT_RANGE_TYPE.MONTHLY_RANGE]: 'MMMM, yyyy',
  };

  useEffect(() => {
    if (id) {
      dispatch(getSonarCollectDataStatus(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <WithLabel label={`${t('dateRanges')}:`} variant={LABEL_STYLES.ROW} isBold>
        <div className={scss.pickerWrapper}>
          <CommonDatePicker
            selectsRange={rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE}
            selected={compareToStartDate ? new Date(compareToStartDate) : null}
            startDate={
              compareToStartDate && rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE
                ? new Date(compareToStartDate)
                : null
            }
            endDate={
              compareToEndDate && rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE ? new Date(compareToEndDate) : null
            }
            dateFormat={timeFormatsByRangeTypes[rangeType]}
            showYearDropdown
            scrollableYearDropdown
            showMonthYearPicker={rangeType === SONAR_PROJECT_RANGE_TYPE.MONTHLY_RANGE}
            yearDropdownItemNumber={100}
            onChange={(dates) => handleChangeDate(SONAR_RANGE_TYPE.COMPARE_TO, dates)}
            minDate={new Date(collectedFrom)}
            maxDate={new Date(collectedTill)}
          />
          <span className={scss.pickerDash}>{'-'}</span>

          <CommonDatePicker
            selectsRange={rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE}
            selected={comparableStartDate ? new Date(comparableStartDate) : null}
            startDate={
              comparableStartDate && rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE
                ? new Date(comparableStartDate)
                : null
            }
            endDate={
              comparableEndDate && rangeType === SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE
                ? new Date(comparableEndDate)
                : null
            }
            dateFormat={timeFormatsByRangeTypes[rangeType] || 'dd.MM.yyyy'}
            showYearDropdown
            scrollableYearDropdown
            showMonthYearPicker={rangeType === SONAR_PROJECT_RANGE_TYPE.MONTHLY_RANGE}
            yearDropdownItemNumber={100}
            onChange={(dates) => handleChangeDate(SONAR_RANGE_TYPE.COMPARABLE, dates)}
            minDate={new Date(collectedFrom)}
            maxDate={new Date(collectedTill)}
          />
        </div>
      </WithLabel>
    </div>
  );
}

export default SonarDatePicker;
